import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Eye,
  Zap,
  Heart,
  Package,
  MessageCircle,
  Shield,
  Sparkles,
  Users,
  Quote,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO from '../seo/SEO';
import { localBusinessSchema } from '../seo/schemas';

const PRINCIPLES = [
  {
    icon: Eye,
    title: 'Transparency, by default',
    body: 'Clear pricing on day one. Real numbers in real time. No surprise surcharges, no fee schedules buried on page nine of a contract. If you can\u2019t explain your invoice in two minutes, something\u2019s wrong.',
  },
  {
    icon: Zap,
    title: 'Operator speed',
    body: 'Same-day shipping isn\u2019t a feature \u2014 it\u2019s the floor. We were the brand calling our 3PL at 4pm asking why orders weren\u2019t out. Now we are the 3PL that picks up the phone before you have to call.',
  },
  {
    icon: Package,
    title: 'Your packaging, your standards',
    body: 'The unboxing is the close. We protect it like it\u2019s our own brand on the box \u2014 because for the years we ran our brands, it was. Custom inserts, branded tape, kitting, gifts with purchase \u2014 it all gets the operator treatment.',
  },
  {
    icon: MessageCircle,
    title: 'Founder-led communication',
    body: 'You won\u2019t get bounced between five tickets. Your account isn\u2019t a number on a queue. Direct line, real responses, and a team that knows your brand by name.',
  },
  {
    icon: Shield,
    title: 'Flexibility over rigidity',
    body: 'Most 3PLs ask you to bend to their system. We bend our process around yours \u2014 from receiving cadences to packing rules to seasonal flows. The boutique approach isn\u2019t a marketing line, it\u2019s the model.',
  },
  {
    icon: Heart,
    title: 'In it for the long haul',
    body: 'We grow when our brands grow. That\u2019s why we\u2019re wired for retention, not for stuffing the warehouse with logos. Quality partnerships, not headcount.',
  },
];

const STATS = [
  { value: '$2', label: 'Per-order pick & pack' },
  { value: '0', label: 'Contracts. Minimums. BS.' },
  { value: '24h', label: 'From estimate to answer' },
  { value: '100%', label: 'Founder-led account access' },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="About Shipmaster Fulfillment | South Florida 3PL"
        description="Operator-led 3PL fulfillment based in Weston, FL. Built by ecommerce founders for Shopify and DTC brands who outgrew their kitchen-table operation."
        keywords="South Florida 3PL, Weston FL fulfillment center, Miami 3PL, operator-led fulfillment, ecommerce founders, boutique 3PL"
        schemas={[localBusinessSchema()]}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-16 pb-20 sm:pt-24 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-10 brand-soft-bg" />
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-40 -right-32 w-[480px] h-[480px] rounded-full bg-indigo-200/40 blur-3xl" />
          <div className="absolute top-32 -left-32 w-[420px] h-[420px] rounded-full bg-blue-200/30 blur-3xl" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-700 text-xs font-bold uppercase tracking-widest shadow-sm">
              <Sparkles className="w-3.5 h-3.5" /> About Shipmaster
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.02]">
              Built by ecommerce operators.{' '}
              <span className="brand-gradient-text">For ecommerce operators.</span>
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-slate-600 font-medium leading-relaxed max-w-2xl">
              Shipmaster wasn&apos;t designed in a boardroom. It was designed in the trenches — from
              years of running our own ecommerce brands and feeling the pain of fulfillment that
              treated us like a number.
            </p>
          </div>
        </div>
      </section>

      {/* Origin story narrative */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionEyebrow>Origin</SectionEyebrow>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            We built the 3PL we wished we&apos;d had.
          </h2>

          <div className="mt-8 space-y-6 text-lg text-slate-700 leading-relaxed">
            <p>
              Before Shipmaster, we were the customer. We ran multiple direct-to-consumer brands,
              shipping thousands of orders a month, and watching our 3PLs treat fulfillment like
              a volume game instead of an extension of the brand we were trying to build.
            </p>
            <p>
              <span className="font-semibold text-slate-900">
                Delayed shipments. Damaged packaging. Communication that came back two days late
                — if at all.
              </span>{' '}
              Rigid systems built for the average customer, optimized for the warehouse, and
              indifferent to the founders trying to scale on the other end.
            </p>
            <p>
              So we did what any operator does when no one else is solving the problem: we built
              it ourselves. What started as solving fulfillment for our own brands evolved into
              helping other growing ecommerce brands ship better, scale smoother, and stop
              dreading the inbox.
            </p>
          </div>

          <Pullquote
            quote="Fulfillment is the last impression your customer has with your brand. We refuse to treat it like a back-office line item."
            attribution="The Shipmaster team"
          />
        </div>
      </section>

      {/* Principles grid */}
      <section className="py-20 sm:py-24 bg-slate-50/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <SectionEyebrow>What we believe</SectionEyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Six principles that run the warehouse.
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Every process inside Shipmaster was designed from an operator&apos;s perspective.
              These aren&apos;t marketing pillars — they’re how we make decisions when nobody&apos;s
              watching.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PRINCIPLES.map((p, idx) => (
              <article
                key={p.title}
                className="group relative bg-white rounded-2xl border border-slate-200 p-7 hover:border-indigo-200 hover:shadow-lg transition-all duration-200 animate-fade-up"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <span className="absolute top-7 right-7 text-xs font-bold tracking-widest text-slate-300 group-hover:text-indigo-300 transition-colors">
                  0{idx + 1}
                </span>
                <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-5 group-hover:bg-indigo-100 transition-colors">
                  <p.icon className="w-6 h-6 text-indigo-600" strokeWidth={2} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-slate-600 leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="brand-gradient rounded-3xl p-10 sm:p-14 text-white overflow-hidden relative">
            <div
              className="absolute inset-0 opacity-15 pointer-events-none"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(135deg, rgba(255,255,255,0.4) 0px, rgba(255,255,255,0.4) 1px, transparent 1px, transparent 18px)',
              }}
              aria-hidden="true"
            />
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 text-center">
              {STATS.map((s) => (
                <div key={s.label}>
                  <p className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
                    {s.value}
                  </p>
                  <p className="mt-2 text-sm sm:text-base font-medium text-indigo-100">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* The boutique 3PL section */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <SectionEyebrow>The boutique 3PL</SectionEyebrow>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
                We chose deep partnerships over a massive warehouse network.
              </h2>
            </div>
            <div className="space-y-5 text-lg text-slate-700 leading-relaxed">
              <p>
                Plenty of 3PLs are sprinting to become the next billion-dollar logistics platform.
                We&apos;re not. We&apos;re building Shipmaster intentionally small, intentionally
                hands-on, and intentionally close to the brands we serve.
              </p>
              <p>
                That means founder-led account access, a team that learns your SKUs by sight, and
                operations that adapt to your launches and seasonality — not a ticketing queue
                routed to whoever has bandwidth.
              </p>
              <p>
                We work best with{' '}
                <span className="font-semibold text-slate-900">
                  emerging and scaling Shopify brands
                </span>{' '}
                that care about customer experience, packaging presentation, and the long-term
                retention numbers that compound when fulfillment is done right.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="py-20 sm:py-24 bg-slate-50/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <SectionEyebrow centered>Who we serve</SectionEyebrow>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              If you ship it, we probably handle it.
            </h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Built for modern ecommerce brands across DTC and B2B — from the brand shipping
              their 200th order this week to the team scaling past 50,000 a month.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            <ServeCard
              title="DTC ecommerce brands"
              body="Shopify, Etsy, WooCommerce, Squarespace, Wix, and TikTok Shop. We connect, sync, and ship same-day."
            />
            <ServeCard
              title="Wholesale & B2B"
              body="Pallet receiving, EDI-friendly workflows, and carton-pack programs that don't choke retail compliance."
            />
            <ServeCard
              title="Subscription & kitting"
              body="Custom inserts, gifts with purchase, recurring boxes, and bundle SKUs assembled exactly to spec."
            />
          </div>
        </div>
      </section>

      {/* Closing manifesto + CTA */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-10 h-10 mx-auto text-indigo-600" />
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Less vendor.{' '}
            <span className="brand-gradient-text">More extension of your team.</span>
          </h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Shipmaster exists because we believed ecommerce brands deserved a fulfillment partner
            that actually understands ecommerce brands. Same-day estimates, no contracts, real
            humans on the other end of the line.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-12 px-7 text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 group"
            >
              <Link to="/#contact" className="inline-flex items-center gap-2">
                Book a Call
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="rounded-full h-12 px-7 border-slate-300 text-slate-800 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
            >
              <Link to="/zones">See our zone map</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function SectionEyebrow({ children, centered }) {
  return (
    <p
      className={`text-xs font-bold uppercase tracking-[0.22em] text-indigo-600 ${
        centered ? 'text-center' : ''
      }`}
    >
      {children}
    </p>
  );
}

function Pullquote({ quote, attribution }) {
  return (
    <figure className="mt-12 relative bg-white border-l-4 border-indigo-600 rounded-r-2xl p-7 sm:p-8 shadow-sm">
      <Quote className="absolute top-5 right-5 w-8 h-8 text-indigo-100" aria-hidden="true" />
      <blockquote className="text-xl sm:text-2xl font-bold text-slate-900 leading-snug">
        &ldquo;{quote}&rdquo;
      </blockquote>
      <figcaption className="mt-4 text-sm font-semibold text-indigo-600 uppercase tracking-widest">
        — {attribution}
      </figcaption>
    </figure>
  );
}

function ServeCard({ title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-7 hover:border-indigo-200 hover:shadow-md transition-all duration-200">
      <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}
